import { isRouteErrorResponse, useRouteError } from "react-router";
import { AlertTriangle, FileQuestion, Lock, RotateCw } from "lucide-react";
import type { ReactNode } from "react";
import { errorMessage, isApiError } from "../api.js";
import { Button, ButtonLink } from "../components/ui/Button.js";
import { useDocumentTitle } from "../lib/hooks.js";

function ErrorLayout({ icon, code, title, children, actions }: {
  icon: ReactNode;
  code?: string;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center px-4 py-12 text-center">
      <div className="mb-3 grid size-14 place-items-center rounded-full bg-surface-3 text-muted [&>svg]:size-7" aria-hidden="true">
        {icon}
      </div>
      {code && <p className="text-[13px] font-semibold tracking-wider text-subtle">{code}</p>}
      <h1 className="mt-1 text-xl font-semibold text-fg">{title}</h1>
      <div className="mt-2 max-w-md text-muted">{children}</div>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {actions ?? (
          <ButtonLink to="/buscar" variant="primary">
            Ir a Buscar
          </ButtonLink>
        )}
      </div>
    </div>
  );
}

export function NotFoundPage() {
  useDocumentTitle("Página no encontrada");
  return (
    <ErrorLayout icon={<FileQuestion />} code="404" title="Página no encontrada">
      La dirección no existe o el contenido ya no está disponible para tu institución.
    </ErrorLayout>
  );
}

export function ForbiddenPage() {
  useDocumentTitle("Acceso restringido");
  return (
    <ErrorLayout icon={<Lock />} code="403" title="Acceso restringido">
      Esta sección es solo para administradores de SENTINEL. Si necesitas acceso, pídelo a un administrador.
    </ErrorLayout>
  );
}

/** Errores de carga de ruta (loader/lazy) o de render: 403/404 con su página; el resto, genérico. */
export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    if (error.status === 403) return <ForbiddenPage />;
    if (error.status === 404) return <NotFoundPage />;
  }
  if (isApiError(error, 403)) return <ForbiddenPage />;
  if (isApiError(error, 404)) return <NotFoundPage />;
  // Un despliegue nuevo cambia los nombres de los módulos diferidos: recargar lo resuelve.
  const chunkFailed = error instanceof TypeError && /dynamically imported module|Importing a module script/i.test(error.message);
  return (
    <ErrorLayout
      icon={<AlertTriangle />}
      title={chunkFailed ? "Hay una versión nueva de SENTINEL" : "Algo salió mal"}
      actions={
        <>
          <Button variant="primary" icon={<RotateCw className="size-4" aria-hidden="true" />} onClick={() => window.location.reload()}>
            Recargar
          </Button>
          <ButtonLink to="/buscar">Ir a Buscar</ButtonLink>
        </>
      }
    >
      {chunkFailed
        ? "Recarga la página para continuar con la versión actualizada."
        : isApiError(error)
          ? errorMessage(error)
          : "Ocurrió un error inesperado al mostrar esta página. Recarga e intenta nuevamente."}
    </ErrorLayout>
  );
}
